import dayjs from 'dayjs';
import { IntlShape } from 'react-intl';
import { ExecutionStatus } from "../../core/enum/ExecutionStatus";
import { PassengerCardEnum, PassengerCardTypeEnum, PassengerPrmNeedTypeEnum, ProductCodeEnum, ProductTypeEnum, ServiceClassTypeEnum, TripDirectionTypeEnum, TripTypeEnum, isShuttleOffer } from "../../Orchestrator/Types/Enums/globalDefaultEnum";
import { AvailableDate, IBookedOffer, IBookingResponse, IDeterminePassenger, IOfferPrice, IPassenger, IPassengerCard, IPassengerSelection, ISelectedPassenger, ITrip, ITripProduct, PassengerPriceDetails, PaymentItemDetails, StationsListType } from "../../Orchestrator/Types/Index";
import { IPassengerType } from '../../Orchestrator/Types/Interface/IPassengerType';
import { IServiceClass, ITrainTypeClass, StopType, TrainCallType, TrainType } from "../../Orchestrator/Types/Types/TrainTypes";
import "dayjs/locale/ar"; // Arabic locale
import "dayjs/locale/en";
import { getIntl, getTranslatedMessage } from './i18n';
import { getLocaleFromCookie } from './getLocaleFromCookie';
import { updateRecoilState } from '../../Orchestrator/Recoils/Events/Recoil';
import { ICMSStops } from '../../Orchestrator/Types/Interface/ICmsStops';
import { ITransportMode } from '../../Orchestrator/Types/Interface/ITransportMode';
import { PassengerTypeEnum } from '../../Orchestrator/Types/Enums/PassengerTypeEnum';
import {
    PASSENGER_ENGLISH_LABEL_TO_ID,
    PASSENGER_ID_TO_I18N_KEY,
    resolveSingularPassengerLabel,
} from './passengerLabelResolver';
import { PlaceMapResponse } from '../../Orchestrator/Types/Types/Checkout/Seating/PlaceMap';
import { getTimeOfDay } from './timeOfDay';

function safeGetRecoilStateValue<T>(key: string, defaultValue?: T): T | undefined {
    if (typeof window === "undefined") return defaultValue;
    try {
        const getter = (window as any)?.__getRecoilStateValue;
        if (typeof getter === "function") {
            const value = getter(key, defaultValue);
            return value as T | undefined;
        }
        return defaultValue;
    } catch {
        return defaultValue;
    }
}

export const isEmpty = (value: string | object | undefined | null) => {
    return (
        value === undefined ||
        value === null ||
        (typeof value === "object" && Object.keys(value).length === 0) ||
        (typeof value === "string" && value.trim().length === 0)
    );
}

/**
 * OSDM minor units → major units.
 *
 * `scale` is "the number of positions after the comma"
 * (docs/retailer.json, `Turnit.Osdm.Contracts.Offers.Price`), so the divisor is
 * 10^scale. This used to be written `amount / ((100 * scale) / scale)`, which
 * algebraically cancels to `amount / 100` — it hardcoded scale 2 and was wrong
 * in two ways:
 *
 *  - scale 0 (a whole-unit amount) divided by `(100*0)/0` = NaN, so a fee of
 *    exactly zero rendered as "NaN" on the cancel receipt;
 *  - any scale other than 2 was off by a factor of ten.
 *
 * For scale 2 — the only value observed on the wire — this is identical to what
 * it replaced, so no displayed price moves.
 *
 * `scale` is `nullable` in the schema, and there is NO safe default for a
 * missing one: assuming 2 turns 6000 into 60 and assuming 0 leaves it at 6000.
 * An unknown scale therefore yields NaN, which surfaces on screen as an obvious
 * error rather than a plausible wrong number. Do not "fix" that with a fallback.
 *
 * A NEGATIVE amount is now scaled like any other. The old `amount > 0` guard
 * returned it as raw minor units instead, i.e. "-6000" where "-60" was meant.
 */
export const calculatePrice = (priceObject: IOfferPrice) => {
    const amount: number = (priceObject?.amount || 0)
    if (amount === 0) return 0
    const scale = priceObject?.scale
    if (typeof scale !== "number" || !Number.isFinite(scale) || scale < 0) return NaN
    return amount / Math.pow(10, scale)
}

/**
 * Passenger-type rows of the cancel page's refund summary, in the order they
 * are listed and under the labels they are listed with. Both row builders (the
 * load-time one in GetBookingAction and updatePaymentItemsForCancelBooking
 * below) read this, so the rows can't reorder or rename themselves when the
 * user touches a checkbox.
 */
export const PASSENGER_TYPE_ROW_LABELS: Record<string, string> = {
    "Adult": "Adult",
    "Children": "Children",
    "Infant": "Infant",
    "Senior Citizen": "Senior Citizen (UAE)",
    "Student": "Student",
    "People with Reduced Mobility": "People with Reduced Mobility",
    "Wheelchair": "Wheelchair",
}
export const PASSENGER_TYPE_ROW_ORDER = Object.keys(PASSENGER_TYPE_ROW_LABELS)

/** Money rounded to fils — a per-passenger share of a collective price repeats. */
export const roundAmount = (amount: number) => Math.round(amount * 100) / 100

/**
 * One entry per passenger covered by an admission. Any admission covering
 * several passengers — COLLECTIVE privacy seating, a group shuttle leg — carries
 * ONE price for the group, so each passenger takes an equal share and the row
 * totals back up to what the backend refunds. Charging every passenger the full
 * price (what the old code did outside the COLLECTIVE case) double-counted.
 * An admission with no passengerIds contributes nothing: there is no row it
 * could belong to.
 */
export const splitAdmissionPerPassenger = (
    admission: { price: IOfferPrice; passengerIds?: string[] }
): { passengerId: string; amount: number }[] => {
    const passengerIds = admission?.passengerIds || []
    if (passengerIds.length === 0) return []
    const share = calculatePrice(admission.price) / passengerIds.length
    return passengerIds.map(passengerId => ({ passengerId, amount: share }))
}

export const getTrainClassTypeBaseOnProduct = (products: ITripProduct[]) => {
    // Find the admission product (the main ticket, not seat reservation)
    const admissionProduct = products.find(p => p.type === ProductTypeEnum.ADMISSION_POINT2POINT) || products[0]
    if (!admissionProduct) return null

    // Determine class from serviceClass.type (dynamic)
    const serviceClassType = admissionProduct.serviceClass?.type
    if (serviceClassType === ServiceClassTypeEnum.STANDARD) return "standardValue"
    if (serviceClassType === ServiceClassTypeEnum.HIGH) return "premium"

    // Fallback: match by product code if serviceClass is missing.
    // Standard- and premium-tier wire codes (see PRODUCT_TIER) collapse to
    // "standardValue"/"premium" here — this helper doesn't distinguish
    // saver vs. value within a tier (see getTrainClassTypeFormProductCode
    // for the finer-grained mapping).
    const code = admissionProduct.code
    if (code === ProductCodeEnum.SFC || code === ProductCodeEnum.SFF || code === ProductCodeEnum.STV) return "standardValue"
    if (code === ProductCodeEnum.PFF || code === ProductCodeEnum.PFP || code === ProductCodeEnum.PRV || code === ProductCodeEnum.PRS) return "premium"
    if (code === ProductCodeEnum.STDNFDUO || code === ProductCodeEnum.STDNFQUAD) return "standardDuo"
    if (code === ProductCodeEnum.PREMNFDUO || code === ProductCodeEnum.PREMNFQUAD) return "premiumDuo"

    return null

}

export const getTrainClassTypeFormProductCode = (productCode: string, serviceClassType?: string) => {
    // Use serviceClass.type if provided (dynamic)
    if (serviceClassType) {
        if (serviceClassType === ServiceClassTypeEnum.STANDARD) return "standardValue"
        if (serviceClassType === ServiceClassTypeEnum.HIGH) return "premium"
    }
    // Fallback to product code matching. Tier mapping mirrors PRODUCT_TIER:
    // - SFC (saver) → "standardValue"
    // - SFF, STV (value) → "standardPro"
    // - PFF, PFP, PRV (premium value), PRS (premium saver) → "premium"
    //
    // Known tier-collapse: PRS (PREMIUM_SAVER) is bucketed with the premium-
    // value codes here because no consumer currently branches on a "premium
    // saver" output class. If Premium Saver badging or pricing ever needs to
    // diverge from Premium Value in the UI, add a new output class (e.g.
    // "premiumSaver"), wire a PRS-only branch here AND in getTrainClassType-
    // FormAPI (kept in lockstep), and add a reverse-map entry in
    // getProductCodeFromTrainClassType.
    if (productCode === ProductCodeEnum.SFC) return `standardValue`
    else if (productCode === ProductCodeEnum.SFF || productCode === ProductCodeEnum.STV) return "standardPro"
    else if (productCode === ProductCodeEnum.PFF || productCode === ProductCodeEnum.PFP || productCode === ProductCodeEnum.PRV || productCode === ProductCodeEnum.PRS) return "premium"
    else return null
}

export const getProductCodeFromTrainClassType = (trainClass: string) => {
    if (trainClass === `standardValue`) return ProductCodeEnum.SFC
    else if (trainClass === "standardPro") return ProductCodeEnum.SFF
    else if (trainClass === "premium") return ProductCodeEnum.PFF
    else return null
}

export const getServiceClassFromTrainClassType = (trainClass: ITrainTypeClass): IServiceClass => {
    if (["standardValue", "standardPro"].includes(trainClass)) {
        return "STANDARD";
    }
    return "PREMIUM";
};

export const toUTC4 = (date: string) => {
    let utcDate = new Date(date);
    utcDate.setUTCHours(utcDate.getUTCHours() + 4);
    return utcDate;
}

export function determineAge(passengerType: string) {
    switch (passengerType.toLowerCase()) {
        case 'adults':
            return 35;
        case 'children':
            return 3;
        case 'infants':
            return 1;
        case 'seniorcitizens':
            return 60;
        case 'students':
            return 19;
        case 'reducedmobility':
            return 20;
        case 'wheelchair':
            return 30;
        default:
            return 35;
    }
}

export function determineMinAge(passengerType: string) {
    const passengerTypes: IPassengerSelection[] = safeGetRecoilStateValue<IPassengerSelection[]>("passengerTypesStateAtom", [] as any) as IPassengerSelection[];
    if (Array.isArray(passengerTypes)) {
        const found = passengerTypes.find(pt => pt.id?.toLowerCase() === passengerType?.toLowerCase());
        if (found && typeof found.minAge === "number") {
            return found.minAge;
        }
    }
}

export function determineDefaultAge(passengerType: string) {
    const passengerTypes: IPassengerSelection[] = safeGetRecoilStateValue<IPassengerSelection[]>("passengerTypesStateAtom", [] as any) as IPassengerSelection[];
    if (Array.isArray(passengerTypes)) {
        const found = passengerTypes.find(pt => pt.id?.toLowerCase() === passengerType?.toLowerCase());
        if (found && typeof found.defaultAge === "number") {
            return found.defaultAge;
        }
    }
    // Fallback to minAge if defaultAge is not available
    return determineMinAge(passengerType);
}

export function determineMaxAge(passengerType: string) {
    const passengerTypes: IPassengerSelection[] = safeGetRecoilStateValue<IPassengerSelection[]>("passengerTypesStateAtom", [] as any) as IPassengerSelection[];
    if (Array.isArray(passengerTypes)) {
        const found = passengerTypes.find(pt => pt.id?.toLowerCase() === passengerType?.toLowerCase());
        if (found && typeof found.maxAge === "number") {
            return found.maxAge;
        }
    }
}

function getAgeBounds(passengerType: string, fallbackMin: number, fallbackMax: number) {
    const min = (determineMinAge(passengerType) as number | undefined);
    const max = (determineMaxAge(passengerType) as number | undefined);
    return {
        min: typeof min === "number" ? min : fallbackMin,
        max: typeof max === "number" ? max : fallbackMax,
    };
}

export function formatDate(dateString: string | number | Date) {
    const date = new Date(dateString);
    const day = date.toLocaleDateString('en-US', { weekday: 'short' });
    const monthDay = date.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
    return `${day}, ${monthDay}`;
}

export function formatDateWithDayDateMonth(dateString: string | number | Date) {
    const date = new Date(dateString);
    const day = date.toLocaleDateString('en-US', { weekday: 'short' });
    const monthDay = date.toLocaleDateString('en-US', { day: '2-digit', month: 'short' }).split(' ').reverse().join(' ');
    return `${day}, ${monthDay}`;
}

export function formatDateWithDayDateMonthForChangeDate(dateString: string | number | Date) {
    const date = new Date(dateString);
    const day = date.toLocaleDateString('en-US', { weekday: 'short' });
    const monthDay = date.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
    return `${day}, ${monthDay}`;
}

export function formatWithDateAndTime(dateString: string | number | Date) {
    const date = new Date(dateString);
    const locale = getIntl().locale;

    const monthsEn = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
    ];

    const monthsAr = [
        "يناير",
        "فبراير",
        "مارس",
        "أبريل",
        "مايو",
        "يونيو",
        "يوليو",
        "أغسطس",
        "سبتمبر",
        "أكتوبر",
        "نوفمبر",
        "ديسمبر",
    ];

    const day = date.getDate();
    const month = locale === "ar" ? monthsAr[date.getMonth()] : monthsEn[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");

    // Convert to Arabic numerals if locale is Arabic
    const toArabicNumerals = (num: number | string): string => {
        const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
        return num.toString().replace(/\d/g, (digit) => arabicNumerals[parseInt(digit)]);
    };

    if (locale === "ar") {
        const dayAr = toArabicNumerals(day);
        const yearAr = toArabicNumerals(year);
        const hoursAr = toArabicNumerals(hours);
        const minutesAr = toArabicNumerals(minutes);
        return `${dayAr} ${month} ${yearAr}، ${hoursAr}:${minutesAr}`;
    }

    return `${day} ${month} ${year}, ${hours}:${minutes}`;
}


// export function getNextDays(nextDays: number): AvailableDate[] {
//     const dates = [];
//     const today = new Date();
//     for (let i = 0; i <= nextDays; i++) {
//         const date = new Date(today);
//         date.setDate(today.getDate() + i);
//         dates.push({
//             day: date.toLocaleDateString('en-US', { weekday: 'short' }),
//             date: formatDateWithDayDateMonth(date),
//             price: "0",
//             fullDate: date.toString()
//         });
//     }
//     return dates;
// }
export function getNextDays(startDate: string, endDate?: string) {
    const dates = []

    const dayJSStart = dayjs(startDate);
    const dayJSEnd = endDate ? dayjs(endDate) : dayJSStart.add(10, 'day'); // Default to 11 days

    const daysDiff = dayJSEnd.diff(dayJSStart, 'day') + 1; // Include end date

    for (let i = 0; i < daysDiff; i++) {
        const current = dayJSStart.add(i, 'day');
        const currentDate = current.toDate();

        dates.push({
            day: currentDate.toLocaleDateString('en-US', { weekday: 'short' }),
            date: formatDateWithDayDateMonth(currentDate),
            price: '0',
            fullDate: currentDate.toString(),
        });
    }

    return dates;
}

/**
 * Converts ISO/UTC time to Arabic time string
 */
export function convertTimeToArabic(isoTime: string = ""): string {
    if (isoTime === "") return ""
    const date = new Date(isoTime);
    return date.toLocaleTimeString("en-AE", {
        timeZone: "Asia/Dubai",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
};

export function convertISO8601ToDuration(isoDuration: string = ""): string {
    if (isoDuration === "") return ""
    const regex = /P(?:(\d+)D)?T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;
    const matches = isoDuration.match(regex);

    if (!matches) {
        return 'Invalid duration';
    }

    const days = parseInt(matches[1] || '0', 10);
    const hours = parseInt(matches[2] || '0', 10);
    const minutes = parseInt(matches[3] || '0', 10);
    const seconds = parseInt(matches[4] || '0', 10);

    let result = '';
    if (days > 0) {
        result += `${days}d `;
    }
    if (hours > 0) {
        result += `${hours}h `;
    }
    if (minutes > 0) {
        result += `${minutes}min `;
    }
    if (seconds > 0) {
        result += `${seconds}s`;
    }

    return result.trim();
}

export function convertISO8601ToMinutes(isoDuration: string = ""): number {
    if (isoDuration === "") return 0
    const regex = /P(?:(\d+)D)?T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;
    const matches = isoDuration.match(regex);

    if (!matches) {
        return 0;
    }

    const days = parseInt(matches[1] || '0', 10);
    const hours = parseInt(matches[2] || '0', 10);
    const minutes = parseInt(matches[3] || '0', 10);
    const seconds = parseInt(matches[4] || '0', 10);

    return (days * 24 * 60) + (hours * 60) + minutes + Math.floor(seconds / 60);
}

export function convertMinutesToDuration(totalMinutes: number = 0): string {
    if (totalMinutes <= 0) return `0${getTranslatedMessage("minText")}`;

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    let result = '';
    if (hours > 0) {
        result += `${hours}${getTranslatedMessage("hText")} `;
    }
    if (minutes > 0) {
        result += `${minutes}${getTranslatedMessage("minText")} `;
    }

    return result.trim();
}

export const getParsePassengerLabel = (passenger: any, intl: any) => {
    // Accepts both legacy "<count> <type>" and the current
    // "<count>x <type>" input forms — callers may not have all migrated.
    // The regex tolerates the optional trailing "x" on the count token.
    const match = typeof passenger === "string"
        ? passenger.match(/^(\d+)x?\s+(.+)$/)
        : null;
    if (!match) return passenger;
    const count = parseInt(match[1], 10);
    let type = match[2];
    if (type === "Senior Citizen") {
        type = "Senior Citizen (UAE)";
    }
    // Special-case Shuttle / Voucher — these aren't passenger types
    // produced by the standard passengerTypes API, so they keep the
    // legacy i18n-key path. Everything else resolves via the API-driven
    // resolver (en-UK/fr-FR from the API, ar-AE from local i18n).
    // Preserve the original Voucher / Shuttle handling — these aren't
    // passenger types from the API, so they keep the legacy i18n-key
    // path (the previous typeMap had Voucher→undefined which the
    // original code already relied on; keep that quirk untouched).
    if (type === "Voucher") {
        return `${intl.formatMessage({ id: (undefined as any) })}`;
    }
    if (type === "Shuttle") {
        const shuttleLabel = intl.formatMessage({ id: "shuttleTicket" });
        return count === 1 ? shuttleLabel : `${count}x ${shuttleLabel}`;
    }
    const passengerId = PASSENGER_ENGLISH_LABEL_TO_ID[type];
    if (!passengerId) return count === 1 ? type : `${count}x ${type}`;
    const label = resolveSingularPassengerLabel(
        passengerId,
        PASSENGER_ID_TO_I18N_KEY[passengerId],
        intl,
    );
    return count === 1 ? label : `${count}x ${label}`;
}


export function filterTrains(trains: TrainType[], filters: string[], type: string): TrainType[] {
    // If filters includes all values or is empty, return all trains
    const filterOptions = ["morning", "afternoon", "evening"];
    if (filters.length === filterOptions.length || filters.length === 0) {
        return trains;
    }

    return trains.filter((train) => {
        const timeOfDay = getTimeOfDay(train.departureTime);
        return timeOfDay !== null && filters.includes(timeOfDay);
    });
}

export const getImageUrl = (trainType: string = "") => {
    if (trainType === "shuttle")
        return "/assets/booking/red-bus.svg"
    if (trainType === "train")
        return "/assets/booking/red-train.svg"

    return ""
}

export const determinePassengerTypeFromPassenger = (passenger: IPassenger): string => {
    const determinePassenger: IDeterminePassenger = {
        age: passenger.age as number || 0,
        cards: passenger?.cards || [],
        prmNeeds: ((passenger?.prmNeeds || []).length > 0 && passenger?.prmNeeds) ? passenger?.prmNeeds[0] : ""
    }
    return determinePassengerType(determinePassenger);
}

export const determinePassengerType = (p: IDeterminePassenger): string => {
    const card: IPassengerCard[] = (p?.cards || []).length > 0 ? p.cards : [];
    const prmNeeds: string = p?.prmNeeds !== "" ? p.prmNeeds : "";

    // A WHEELCHAIR prm need must outrank the discount card. PROD attaches the
    // POD ("Person of Determination") discount card to wheelchair passengers
    // (INT does not), and PassengerCardEnum.ReducedMobility is also "POD" — so
    // a card-first check classifies these passengers as 'reducedMobility', a
    // type with no checkout slot. SelectPassengerAction then can't match the
    // 'wheelchair' slot and leaves it unwired, and the guest positional
    // fallback aliases it onto passengers[0] (the adult), syncing both name
    // fields. Resolving the wheelchair prm need first keeps the booked
    // wheelchair passenger on its own slot.
    if (prmNeeds != "" && prmNeeds == PassengerPrmNeedTypeEnum.Wheelchair) {
        return 'wheelchair';
    }

    if (card?.length > 0) {
        if (card.find(d => d.code == PassengerCardEnum.Student)) {
            return 'students';
        }
        else if (card.find(d => d.code == PassengerCardEnum.ReducedMobility)) {
            return 'reducedMobility';
        }
        else if (card.find(d => d.code == PassengerCardEnum.Infant)) {
            return 'infants';
        }
    }

    const hasValidAge = typeof p.age === 'number' && !isNaN(p.age);
    if (!hasValidAge) {
        return 'adults'
    }
    else {
        const children = getAgeBounds('children', 0, 16);
        const adults = getAgeBounds('adults', 17, 150);
        const students = getAgeBounds('students', 19, 24);
        const seniors = getAgeBounds('seniorcitizens', 60, 140);
        const reduced = getAgeBounds('reducedMobility', 2, 150);
        const wheelchair = getAgeBounds('wheelchair', 17, 150);
        // Check seniors before adults: the fallback adults range (17-150) is a
        // superset of seniors (60-140), so age 65 would otherwise classify as
        // 'adults' and SelectPassengerAction would never resolve a senior slot.
        if (p.age >= children.min && p.age <= children.max) return 'children';
        else if (p.age >= seniors.min && p.age <= seniors.max) return 'seniorcitizens';
        else if ((p.age >= adults.min && p.age <= adults.max)) return 'adults';
        else if ((p.age >= students.min && p.age <= students.max)) return 'students';
        else if (p.age >= reduced.min && p.age <= reduced.max) return 'reducedMobility';
        else if (p.age >= wheelchair.min && p.age <= wheelchair.max) return 'wheelchair';
    }

    return 'adults';
};

export const determineIsDOBRequiredForPassengerType = (passengerType: string): boolean => {
    return passengerType == 'infants' || passengerType == 'children' || passengerType == 'seniorcitizens'
};

export const getUpperPassengerLabelByAge = (age: number, passenger: ISelectedPassenger): string => {
    let type = ""
    const prmNeeds = ((passenger?.prmNeeds || []).length > 0 && passenger?.prmNeeds) ? passenger?.prmNeeds[0] : "";
    const determinePassenger: IDeterminePassenger = {
        age: passenger.age as number,
        cards: passenger?.cards || [],
        prmNeeds: prmNeeds
    }
    const passengerGroup = determinePassengerType(determinePassenger)
    if (passengerGroup.includes("adults")) {
        type = "Adult"
    } else if (passengerGroup.includes("children")) {
        type = "Children"
    } else if (passengerGroup.includes("infants")) {
        type = "Infant"
    } else if (passengerGroup.includes("student")) {
        type = "Student"
    } else if (passengerGroup.includes("seniorcitizens")) {
        type = "Senior Citizen"
    } else if (passengerGroup.includes("wheelchair")) {
        type = "Wheelchair"
    } else if (passengerGroup.includes("reducedMobility")) {
        type = "People with Reduced Mobility"
    }
    return type
}

export const getPassengerTypeValueFromType = (passengerType: string): PassengerTypeEnum | null => {
    let type = null
    if (passengerType.includes("adults")) {
        type = PassengerTypeEnum.adult
    } else if (passengerType.includes("children")) {
        type = PassengerTypeEnum.child
    } else if (passengerType.includes("infants")) {
        type = PassengerTypeEnum.infant
    } else if (passengerType.includes("student")) {
        type = PassengerTypeEnum.student
    } else if (passengerType.includes("seniorcitizens")) {
        type = PassengerTypeEnum.senior
    } else if (passengerType.includes("wheelchair")) {
        type = PassengerTypeEnum.wheelchair
    } else if (passengerType.includes("reducedMobility")) {
        type = PassengerTypeEnum.prm
    }
    return type
}

export const getPassengerTypeValue = (passenger: ISelectedPassenger): PassengerTypeEnum | null => {
    let type = null
    const prmNeeds = ((passenger?.prmNeeds || []).length > 0 && passenger?.prmNeeds) ? passenger?.prmNeeds[0] : "";
    const determinePassenger: IDeterminePassenger = {
        age: passenger.age as number,
        cards: passenger?.cards || [],
        prmNeeds: prmNeeds
    }
    const passengerGroup = determinePassengerType(determinePassenger)
    return getPassengerTypeValueFromType(passengerGroup)
}


export const getPassengerLabel = (item: any, intl: any) => {
    const { passengerCnt: count, passengerType: type } = item;

    // Format (per product spec):
    //   count === 1 → just the singular type label ("Adult")
    //   count >  1 → "<count>x <singular label>" ("6x Adult", "5x Wheelchair")
    // The PTC (singular label) is sourced dynamically — API-provided for
    // en-UK/fr-FR, local i18n for ar-AE — via the shared resolver, so
    // labels stay in lockstep with the Transcity backend.
    const i18nKey = PASSENGER_ID_TO_I18N_KEY[type];
    const label = resolveSingularPassengerLabel(type, i18nKey, intl);
    return count === 1 ? label : `${count}x ${label}`;
}

export const calculateAgeGroup = (passengers: IPassenger[], intl?: any, getPassengerType?: boolean): string => {
    let text = "";
    if (passengers) {
        //Adult
        const allAdultMember = passengers.filter((pass) => {
            return determinePassengerType({ age: pass.age, cards: pass.cards, prmNeeds: pass.prmNeeds?.length > 0 ? pass.prmNeeds[0] : "" }) == "adults"
        });
        //Child
        const allChildMember = passengers.filter((pass) => {
            return determinePassengerType({ age: pass.age, cards: pass.cards, prmNeeds: pass.prmNeeds?.length > 0 ? pass.prmNeeds[0] : "" }) == "children"
        });
        //Infant
        const allInfantMember = passengers.filter((pass) => {
            return determinePassengerType({ age: pass.age, cards: pass.cards, prmNeeds: pass.prmNeeds?.length > 0 ? pass.prmNeeds[0] : "" }) == "infants"
        });
        //Senior
        const allSeniorMember = passengers.filter((pass) => {
            return determinePassengerType({ age: pass.age, cards: pass.cards, prmNeeds: pass.prmNeeds?.length > 0 ? pass.prmNeeds[0] : "" }) == "seniorcitizens"
        });
        //Student
        const allStudentMember = passengers.filter((pass) => {
            return determinePassengerType({ age: pass.age, cards: pass.cards, prmNeeds: pass.prmNeeds?.length > 0 ? pass.prmNeeds[0] : "" }) == "students"
        });
        //People with reduce mobility
        const allPRMMember = passengers.filter((pass) => {
            return determinePassengerType({ age: pass.age, cards: pass.cards, prmNeeds: pass.prmNeeds?.length > 0 ? pass.prmNeeds[0] : "" }) == "reducedMobility"
        });
        //Wheelchair
        const allWheelchairMember = passengers.filter((pass) => {
            return determinePassengerType({ age: pass.age, cards: pass.cards, prmNeeds: pass.prmNeeds?.length > 0 ? pass.prmNeeds[0] : "" }) == "wheelchair"
        });

        // Count how many different passenger types we have
        const passengerTypes = [
            allAdultMember.length > 0,
            allChildMember.length > 0,
            allInfantMember.length > 0,
            allSeniorMember.length > 0,
            allStudentMember.length > 0,
            allPRMMember.length > 0,
            allWheelchairMember.length > 0
        ].filter(Boolean).length;

        // If there are multiple passenger types, show total count as "X passengers"
        if (passengerTypes > 1) {
            const totalPassengers = passengers.length;
            if (getPassengerType) {
                return `${totalPassengers} Passengers`;
            } else {
                // Use the same pattern as getPassengerLabel for consistency
                return `${totalPassengers} ${intl ? intl.formatMessage({ id: totalPassengers === 1 ? 'passengerText' : 'passengersText' }) : 'passengers'}`;
            }
        }

        // If there's only one type, show the detailed breakdown as before
        text = [
            allAdultMember.length && (getPassengerType ?
                `${allAdultMember.length} Adult` :
                getPassengerLabel({ passengerCnt: allAdultMember.length, passengerType: "adults" }, intl)
            ),
            allChildMember.length && (getPassengerType ?
                `${allChildMember.length} Child` :
                getPassengerLabel({ passengerCnt: allChildMember.length, passengerType: "children" }, intl)
            ),
            allInfantMember.length && (getPassengerType ?
                `${allInfantMember.length} Infant` :
                getPassengerLabel({ passengerCnt: allInfantMember.length, passengerType: "infants" }, intl)
            ),
            allSeniorMember.length && (getPassengerType ?
                `${allSeniorMember.length} Senior Citizen` :
                getPassengerLabel({ passengerCnt: allSeniorMember.length, passengerType: "seniorcitizens" }, intl)
            ),
            allStudentMember.length && (getPassengerType ?
                `${allStudentMember.length} Student` :
                getPassengerLabel({ passengerCnt: allStudentMember.length, passengerType: "students" }, intl)
            ),
            allPRMMember.length && (getPassengerType ?
                `${allPRMMember.length} Person with Reduced Mobility` :
                getPassengerLabel({ passengerCnt: allPRMMember.length, passengerType: "reducedMobility" }, intl)
            ),
            allWheelchairMember.length && (getPassengerType ?
                `${allWheelchairMember.length} Wheelchair` :
                getPassengerLabel({ passengerCnt: allWheelchairMember.length, passengerType: "wheelchair" }, intl)
            )
        ].filter(Boolean).join(", ");
    }
    return text;
};

export const getShortDateFromDateString = (dateString: string = "") => {
    if (dateString === "") return ""
    return new Date(dateString).toLocaleDateString(
        "en-US",
        {
            weekday: "short",
            day: "2-digit",
            month: "short",
        }
    )
};

export const getShortDateFromDateStringFormatted = (dateString: string = "") => {
    if (dateString === "") return "";

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "";

    // Arabic design (Figma): full Arabic weekday + numeric DD/MM — e.g. "الثلاثاء، 02/06".
    if (getLocaleFromCookie() === "ar") {
        const weekday = date.toLocaleDateString("ar-AE", { weekday: "long" });
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        return `${weekday}، ${day}/${month}`;
    }

    const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
    const day = date.toLocaleDateString("en-US", { day: "2-digit" });
    const month = date.toLocaleDateString("en-US", { month: "short" });

    return `${weekday}, ${day} ${month}`;
};

export const extractCalls = (timedLeg: ITrip["legs"][number]["timedLeg"]): TrainCallType[] =>
    (timedLeg?.intermediates || []).map((call) => ({
        stopPlaceRef: call.stopPlaceRef?.stopPlaceRef || "",
        name: call.stopPlaceName || "",
        time: convertTimeToArabic(call.serviceArrival?.timetabledTime || call.serviceDeparture?.timetabledTime),
    }));

export const extractStops = (trip: ITrip) => {
    const stops: StopType[] = [];
    const totalLegs = trip.legs.length;

    trip.legs.forEach((leg, index) => {
        const timedLeg = leg.timedLeg;
        if (!timedLeg) return;

        const start = timedLeg.start;
        const end = timedLeg.end;

        // Add start location
        if (index === 0) {
            // First station
            stops.push({
                stopPlaceRef: start.stopPlaceRef.stopPlaceRef,
                name: start.stopPlaceName,
                time: convertTimeToArabic(start.serviceDeparture.timetabledTime),
                trainNumber: timedLeg.service.vehicleNumbers.join(", "),
                statusType: "normal",
                duration: getStopDuration(
                    start.serviceDeparture.timetabledTime,
                    end.serviceArrival.timetabledTime
                ),
                durationInMinutes: Math.abs((new Date(end.serviceArrival.timetabledTime).getTime() - new Date(start.serviceDeparture.timetabledTime).getTime()) / (1000 * 60)),
                isShuttle: timedLeg.service.mode.ptMode === "BUS",
                calls: extractCalls(timedLeg)
            });
        }

        // Handle intermediate stops
        if (index < totalLegs - 1) {
            const nextLeg = trip.legs[index + 1];
            const nextStart = nextLeg?.timedLeg?.start;

            // Add arrival at intermediate stop
            stops.push({
                stopPlaceRef: end.stopPlaceRef.stopPlaceRef,
                name: end.stopPlaceName,
                time: convertTimeToArabic(end.serviceArrival.timetabledTime),
                trainNumber: "",
                statusType: "stop_over",
                statusTime: getStopDuration(
                    end?.serviceArrival?.timetabledTime,
                    nextLeg?.timedLeg?.start?.serviceDeparture?.timetabledTime
                ),
                durationInMinutes: Math.abs((new Date(nextLeg?.timedLeg?.start?.serviceDeparture?.timetabledTime).getTime() - new Date(end?.serviceArrival?.timetabledTime).getTime()) / (1000 * 60))
            });

            // Add departure from intermediate stop
            if (nextStart) {
                stops.push({
                    stopPlaceRef: nextStart.stopPlaceRef.stopPlaceRef,
                    name: nextStart.stopPlaceName,
                    time: convertTimeToArabic(nextStart.serviceDeparture.timetabledTime),
                    trainNumber: nextLeg.timedLeg.service.vehicleNumbers.join(", "),
                    statusType: "normal",
                    duration: getStopDuration(
                        nextStart.serviceDeparture.timetabledTime,
                        nextLeg.timedLeg.end.serviceArrival.timetabledTime
                    ),
                    durationInMinutes: Math.abs((new Date(nextLeg.timedLeg.end.serviceArrival.timetabledTime).getTime() - new Date(nextStart.serviceDeparture.timetabledTime).getTime()) / (1000 * 60)),
                    isShuttle: nextLeg?.timedLeg?.service.mode.ptMode === "BUS",
                    calls: extractCalls(nextLeg.timedLeg)
                });
            }
        } else {
            // Add final destination
            stops.push({
                stopPlaceRef: end.stopPlaceRef.stopPlaceRef,
                name: end.stopPlaceName,
                time: convertTimeToArabic(end.serviceArrival.timetabledTime),
                trainNumber: timedLeg.service.vehicleNumbers.join(", "),
                statusType: "last_stop"
            });
        }
    });

    return stops;
}

export const getStopDuration = (startTime: string, endTime: string) => {
    if (!startTime || !endTime) {
        return "";
    }

    const startTimeDate = new Date(startTime);
    const endTimeDate = new Date(endTime);

    const durationInMinutes = Math.floor((endTimeDate.getTime() - startTimeDate.getTime()) / (1000 * 60));

    return convertMinutesToDuration(durationInMinutes)
}

export const isActionRunSuccessfully = (action: any) => {
    return action.status.executionStatus === ExecutionStatus.SUCCESS
}

/**
 * Resolves the reduction-card code for a passenger type from the passenger-type
 * config (passengerTypesStateAtom) instead of hardcoding it. The backend drives
 * this via the `discountCard` field (e.g. infant -> "INF", wheelchair -> "POD",
 * senior -> "Senior").
 */
export function determineDiscountCard(passengerType: string): string | undefined {
    const passengerTypes: IPassengerSelection[] = safeGetRecoilStateValue<IPassengerSelection[]>("passengerTypesStateAtom", [] as any) as IPassengerSelection[];
    if (Array.isArray(passengerTypes)) {
        const found = passengerTypes.find(pt => pt.id?.toLowerCase() === passengerType?.toLowerCase());
        if (found && found.discountCard) {
            return found.discountCard;
        }
    }
    return undefined;
}

export const getPassengerCard = (passengerType: string) => {
    const discountCard = determineDiscountCard(passengerType);
    if (discountCard) return [
        {
            code: discountCard,
            type: PassengerCardTypeEnum.ReductionCard
        }
    ]
    return []
}

/**
 * This function basically used to identify the passenger type based on the age, cards and prmNeeds.
 * It returns a string that can be used for translation purposes.
*/
export const passengerTypeWithTranslation = (p: IDeterminePassenger): string => {

    const card: IPassengerCard[] = (p?.cards || []).length > 0 ? p.cards : [];
    const prmNeeds: string = p?.prmNeeds !== "" ? p.prmNeeds : "";

    if (card?.length > 0) {
        if (card.find(d => d.code == PassengerCardEnum.Student)) {
            return 'studentLabel';
        }
        else if (card.find(d => d.code == PassengerCardEnum.ReducedMobility)) {
            return 'reduced_mobility';
        }
        else if (card.find(d => d.code == PassengerCardEnum.Infant)) {
            return 'infant';
        }
    }

    if (prmNeeds != "" && prmNeeds == PassengerPrmNeedTypeEnum.Wheelchair) {
        return 'personWheelchair';
    }

    const hasValidAge = typeof p.age === 'number' && !isNaN(p.age);
    if (!hasValidAge) {
        return 'adultLabel'
    }
    else {
        const children = getAgeBounds('children', 0, 16);
        const adults = getAgeBounds('adults', 17, 150);
        const students = getAgeBounds('students', 19, 24);
        const seniors = getAgeBounds('seniorcitizens', 60, 140);
        const reduced = getAgeBounds('reducedMobility', 2, 150);
        const wheelchair = getAgeBounds('wheelchair', 17, 150);
        // Seniors before adults — mirrors the order in determinePassengerType.
        if (p.age >= children.min && p.age <= children.max) return 'children';
        else if (p.age >= seniors.min && p.age <= seniors.max) return 'senior_citizens';
        else if ((p.age >= adults.min && p.age <= adults.max)) return 'adultLabel';
        else if ((p.age >= students.min && p.age <= students.max)) return 'studentLabel';
        else if (p.age >= reduced.min && p.age <= reduced.max) return 'reduced_mobility';
        else if (p.age >= wheelchair.min && p.age <= wheelchair.max) return 'wheelchair';
    }

    return 'adultLabel';

};

export const getPassengerAge = (pass: IDeterminePassenger) => {
    try {
        if (pass) {
            let passengerAgeGroup = determinePassengerType(pass);
            if (passengerAgeGroup === "student") passengerAgeGroup = "students"
            const age = determineAge(passengerAgeGroup);
            return age;
        }
        return 12 // default age for adult   
    } catch (error) {
        return 12; // default age for adult in case of error
    }
}

export { TTL } from "./TTL";

export const getAgeRange = (type: IPassengerType) => {
    if (type?.maxAge || type?.minAge) {
        if (("minAge" in type) && isEmpty((type?.maxAge || "").toString()))
            return `${type?.minAge}+`
        else if (("minAge" in type) && "maxAge" in type)
            return `${type.minAge} - ${type.maxAge}`
    }
    return ""
}

export const dayOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export const dayOrderArabic = [
    "الاثنين",    // Monday
    "الثلاثاء",    // Tuesday
    "الأربعاء",   // Wednesday
    "الخميس",     // Thursday
    "الجمعة",     // Friday
    "السبت",      // Saturday
    "الأحد"       // Sunday
];

export const arabicDayNames: { [key: string]: string } = {
    "Mon": "الاثنين",
    "Tue": "الثلاثاء",
    "Wed": "الأربعاء",
    "Thu": "الخميس",
    "Fri": "الجمعة",
    "Sat": "السبت",
    "Sun": "الأحد"
};

export const arabicMonthNames: { [key: string]: string } = {
    Jan: "يناير",
    Feb: "فبراير",
    Mar: "مارس",
    Apr: "أبريل",
    May: "مايو",
    Jun: "يونيو",
    Jul: "يوليو",
    Aug: "أغسطس",
    Sep: "سبتمبر",
    Oct: "أكتوبر",
    Nov: "نوفمبر",
    Dec: "ديسمبر",
};


export const formatDateByLocale = (date: any) => {
    const dayjs = require("dayjs");
    require("dayjs/locale/ar");
    require("dayjs/locale/en");
    const localizedDayjs = dayjs(date).locale("en");
    const locale = getIntl().locale
    if (locale === "ar") {
        const dayName = arabicDayNames[localizedDayjs.format("ddd")];
        const day = localizedDayjs.format("DD");
        const month = localizedDayjs.format("MM");
        return `${dayName}، ${day}/${month}`;
    }
    return localizedDayjs.format("ddd, DD MMM");
};


export const formatDayAndMonthByLocale = (date: any) => {
    const dayjs = require("dayjs");
    require("dayjs/locale/ar");
    require("dayjs/locale/en");
    const localizedDayjs = dayjs(date).locale("en");
    const locale = getIntl().locale
    if (locale === "ar") {
        const dayName = arabicDayNames[localizedDayjs.format("ddd")];
        const day = localizedDayjs.format("DD");
        const monthName = arabicMonthNames[localizedDayjs.format("MMM")];
        return `${dayName}، ${day} ${monthName}`;
    }
    return localizedDayjs.format("ddd, DD MMM");
};




// Amenity codes are matched case-insensitively, so every key here MUST be
// uppercase: the backend mixes conventions (CARP, METRO, but carRental), and a
// case mismatch silently drops the icon. Codes that differ between
// environments are aliased onto one asset rather than normalised.
export const AMENITY_ICONS: Record<string, string> = {
  TVM: "/assets/amenities/tvm.svg",
  INFO: "/assets/amenities/info.svg",
  PREL: "/assets/amenities/prel.svg",
  WAIT: "/assets/amenities/wait.svg",
  FAMR: "/assets/amenities/famr.svg",
  FIRS: "/assets/amenities/firs.svg",
  RETL: "/assets/amenities/retl.svg",
  FDRT: "/assets/amenities/fdrt.svg",
  TOIL: "/assets/amenities/toil.svg",
  PRAY: "/assets/amenities/pray.svg",
  ACCE: "/assets/amenities/acce.svg",
  LTPK: "/assets/amenities/carp.svg",
  CARP: "/assets/amenities/carp.svg",
  CARR: "/assets/amenities/carr.svg",
  STPK: "/assets/amenities/stpk.svg",
  MOBP: "/assets/amenities/mobp.svg",
  LEVP: "/assets/amenities/levp.svg",
  SHUB: "/assets/amenities/shub.svg",
  BUST: "/assets/amenities/shub.svg",
  TAXI: "/assets/amenities/taxi.svg",
  METRO: "/assets/amenities/metro.svg",
  WIFI: "/assets/amenities/wif.svg",
  TBM: "/assets/amenities/tbm.svg",
  CARRENTAL: "/assets/amenities/carr.svg",
};

export const getAmenitiesIconByName = (code?: string) => {
    const key = code?.toUpperCase();
    return key ? AMENITY_ICONS[key] ?? "" : "";
}

// Helper function to show loader
export function showLoader() {
    if (typeof window !== "undefined") {
        const event = new CustomEvent("loader", { detail: "show" });
        window.dispatchEvent(event);
    }
}

// Helper function to hide loader
export function hideLoader() {
    if (typeof window !== "undefined") {
        const event = new CustomEvent("loader", { detail: "hide" });
        window.dispatchEvent(event);
    }
}

export const isInfantPassenger = (passenger: any): boolean => {
    const card: any[] = (passenger?.cards || []).length > 0 ? passenger.cards : [];
    const infantBounds = getAgeBounds('infants', 0, 3);
    const infantDiscountCard = determineDiscountCard('infants') ?? PassengerCardEnum.Infant;
    return (passenger.age >= infantBounds.min && passenger.age <= infantBounds.max) && card.some(c => c?.code === infantDiscountCard);
};

export const isChildrenPassenger = (passenger: any): boolean => {
    const childBounds = getAgeBounds('children', 0, 16);
    return (passenger.age >= childBounds.min && passenger.age <= childBounds.max) && (passenger?.cards || [])?.length == 0;
};

export const shouldSharePlaceWithGuardian = (passenger: any): boolean => {
    return isInfantPassenger(passenger);
};

//This function will check given time is within threshold or not. and Show the booking options accordingly.
export const isDepartureTimeWithinThreshold = (departureDateTime: string, hoursThreshold?: number): boolean => {
    const departureDate = new Date(departureDateTime);
    const now = new Date();

    const threshold = hoursThreshold ||
        (process.env.NEXT_PUBLIC_AFTERSALES_CHANGEDATE_HOURS ?
            parseInt(process.env.NEXT_PUBLIC_AFTERSALES_CHANGEDATE_HOURS) : 1);

    // Check if the departure date is more than the threshold hours from now
    const timeDifference = departureDate.getTime() - now.getTime();
    const hoursDifference = timeDifference / (1000 * 60 * 60);
    return hoursDifference > threshold;
};

export const translateDuration = (duration: string, intl: IntlShape) => {
    if (duration) {
        const regex = /^(?:(\d+)d)?\s?(?:(\d+)h)?\s?(?:(\d+)min)?\s?(?:(\d+)s)?$/;
        const matches = duration.match(regex);
        // const matches = "1d 3h 45min 5s".match(regex);
        if (!matches) {
            return "";
        }
        const durationParts = matches; // "1d 3h 45min 5s".split(" ");
        const days = parseInt(durationParts[1], 10);
        const hours = parseInt(durationParts[2], 10);
        const minutes = parseInt(durationParts[3], 10);
        const seconds = parseInt(durationParts[4], 10);
        let durationStr = [];
        if (days > 0) {
            durationStr.push(`${days}${intl.formatMessage({ id: "dText" })}`);
        }
        if (hours > 0) {
            durationStr.push(`${hours}${intl.formatMessage({ id: "hText" })}`);
        }
        if (minutes > 0) {
            durationStr.push(`${minutes}${intl.formatMessage({ id: "minText" })}`);
        }
        if (seconds > 0) {
            durationStr.push(`${seconds}${intl.formatMessage({ id: "sText" })}`);
        }
        return durationStr.join(" ");
    }
    return ""
}

export const filterTripsAndOffersWithPrebookStatus = <T extends { id: string }, U extends { admissions?: Array<{ status: string }> }>(
    trips: T[],
    bookedOffers: U[]
): { filteredTrips: T[]; filteredBookedOffers: U[] } => {
    const filteredBookedOffers = bookedOffers.filter(offer =>
        // Onward shuttles are deliberately NOT excluded: this feeds the
        // isPrebookedOnly path used by Add-shuttle-transfer, where the shuttle
        // is precisely the offer being charged.
        // Defensive: `admissions` is non-optional on IBookedOffer but is absent
        // on some wire payloads, and this runs on the isPrebookedOnly path where
        // that crashed. Same guard as the sibling filters in
        // PrepareBookingSummaryAction. (It does NOT make aftersales Nol billable
        // — see ONWARD_NOL_ADD_ENABLED for why that is still off.)
        (offer.admissions?.length ?? 0) > 0 &&
        (offer.admissions ?? []).every(admission => admission.status === "PREBOOKED")
    );

    const coveredLegIds = new Set(
        filteredBookedOffers.flatMap(offer =>
            (offer as any).tripCoverage?.coveredLegIds || []
        )
    );

    const filteredTrips = trips.filter(trip => {
        return (trip as any).legs?.some((leg: any) =>
            coveredLegIds.has(leg.id)
        );
    });

    return {
        filteredTrips,
        filteredBookedOffers
    };
}
export const translateValue = (key: string, intl: IntlShape) => {
    let text = ""
    if (key)
        text = intl.formatMessage({ id: key })
    return text
}

/**
 * @value sdl43601-5d24d-fds4-kf95-ls8g7hf5hd67 is used to determine that,
 * Service request page is without booking details page.
 * Without booking details page is open from the Help page. 
*/
export const isWithoutBookingServiceRequest = (id: string) => {
    return id === "sdl43601-5d24d-fds4-kf95-ls8g7hf5hd67"
}

export function convertPassengersToCount(passengers: IPassenger[]): Record<string, number> {
    const counts: Record<string, number> = {};
    passengers.forEach(passenger => {
        const determinePassenger: IDeterminePassenger = {
            age: passenger.age || 0,
            cards: passenger?.cards || [],
            prmNeeds: ((passenger?.prmNeeds || []).length > 0 && passenger?.prmNeeds) ? passenger?.prmNeeds[0] : ""
        }
        const type = determinePassengerType(determinePassenger);
        counts[type] = (counts[type] || 0) + 1;
    });
    return counts;
}

export function getNextDaysForBookReturnJourney(selectedDate: string): AvailableDate[] {
    const dates: AvailableDate[] = [];
    const today = dayjs().startOf('day');
    const maxDays = dayjs().add(90, 'days');
    let current = dayjs(selectedDate);
    while (current.isBefore(maxDays) || current.isSame(maxDays, 'day')) {
        dates.push({
            day: current.toDate().toLocaleDateString('en-US', { weekday: 'short' }),
            date: formatDate(current.toDate()),
            price: "0",
            fullDate: current.toDate().toString()
        });
        current = current.add(1, 'day');
    }
    return dates;
}

export function filterBookingByOfferId(bookingResponse: IBookingResponse, targetOfferId: string | string[]): IBookingResponse {
    const filteredBooking = JSON.parse(JSON.stringify(bookingResponse)); // Deep clone

    const targetOfferIds = Array.isArray(targetOfferId) ? targetOfferId : [targetOfferId];

    // Legs the target offers cover — a Nol pass bought for one of them lands on
    // its OWN new booked offer, whose id is not in targetOfferIds. Filtering by
    // id alone therefore dropped the pass the passenger had just added (and the
    // charge for it) the moment the booking was re-read.
    const scopedLegIds = new Set<string>();
    filteredBooking.booking.bookedOffers
        .filter((offer: any) => targetOfferIds.includes(offer.offerId))
        .forEach((offer: any) =>
            (offer.admissions || []).forEach((admission: any) =>
                (admission?.tripCoverage?.coveredLegIds || []).forEach((legId: string) =>
                    scopedLegIds.add(legId)
                )
            )
        );
    const keepsScopedPass = (offer: any) =>
        (offer.admissions || []).some(
            (admission: any) =>
                admission?.isFmLmTravelPass === true &&
                (admission?.tripCoverage?.coveredLegIds || []).some((legId: string) =>
                    scopedLegIds.has(legId)
                )
        );

    // Filter booked offers to include all matching offers
    filteredBooking.booking.bookedOffers = (filteredBooking.booking.bookedOffers || []).filter(
        (offer: any) => targetOfferIds.includes(offer.offerId) || keepsScopedPass(offer)
    );

    // If no matching offer found, return empty structure
    if (filteredBooking.booking.bookedOffers.length === 0) {
        return filteredBooking;
    }

    const coveredTripIds = new Set<string>();
    filteredBooking.booking.bookedOffers.forEach((offer: any) => {
        if (offer.tripCoverage?.coveredTripId) {
            coveredTripIds.add(offer?.tripCoverage?.coveredTripId);
        }
    });

    filteredBooking.booking.trips = (filteredBooking.booking.trips || []).filter(
        (trip: any) => coveredTripIds.has(trip.id)
    );

    const targetAdmissionIds = new Set<string>();
    const targetReservationIds = new Set<string>();
    
    filteredBooking.booking.bookedOffers.forEach((offer: any) => {
        offer.admissions?.forEach((admission: any) => {
            if (admission.id) targetAdmissionIds.add(admission.id);
        });
        offer.reservations?.forEach((reservation: any) => {
            if (reservation.id) targetReservationIds.add(reservation.id);
        });
    });

    const targetIds = [...targetAdmissionIds, ...targetReservationIds];

    // Guarded: this now runs on EVERY booking refresh (storeBookingResponseAtom
    // re-applies the active scope), so a response without fulfillments must not
    // take the atom write down with it.
    filteredBooking.booking.fulfillments = (filteredBooking.booking.fulfillments || []).filter(
        (fulfillment: any) => targetIds.includes(fulfillment.id)
    );

    const passengerIds = new Set<string>();
    filteredBooking.booking.bookedOffers.forEach((offer: any) => {
        offer.admissions?.forEach((admission: any) => {
            admission.passengerIds?.forEach((id: string) => passengerIds.add(id));
        });
        offer.reservations?.forEach((reservation: any) => {
            reservation.passengerIds?.forEach((id: string) => passengerIds.add(id));
        });
    });

    filteredBooking.booking.passengers = (filteredBooking.booking.passengers || []).filter(
        (passenger: any) => passengerIds.has(passenger.id)
    );

    return filteredBooking;
}

//This function is for Cancel Booking Payment Summary
export function calculateTrainAndShuttlePricesFromBooking(
    booking: IBookingResponse,
    selectedPassengerIds: string[],
    direction: TripTypeEnum.OUT_BOUND | TripTypeEnum.IN_BOUND
): PassengerPriceDetails[] {
    if (!booking?.booking) {
        return [];
    }
    const allBookedOffers = booking.booking.bookedOffers || [];
    const allTrips = booking.booking.trips || [];
    const allPassengers = booking.booking.passengers || [];

    const validOffers = allBookedOffers.filter(offer => {
        const hasValidAdmissions = offer.admissions?.some(admission => 
            admission.status !== "EXCHANGED" && admission.status !== "EXCHANGE_ONGOING"
        );
        return hasValidAdmissions;
    });

    const directionTrips = allTrips.filter(trip => trip.direction === direction);
    const directionTripIds = new Set(directionTrips.map(trip => trip.id));

    const directionOffers = validOffers.filter(offer => 
        directionTripIds.has(offer.tripCoverage?.coveredTripId)
    );

    // Match GetBookingAction: a shuttle offer is any offer whose NON-ancillary
    // products include a shuttle code (see isShuttleOffer), not just one whose
    // products[0] is the shuttle. Checking index 0 missed offers that list the
    // shuttle second, so its price was summed into the fare row and the shuttle
    // line vanished from the refund summary.
    const trainOffers = directionOffers.filter(offer => !isShuttleOffer(offer));
    const shuttleOffers = directionOffers.filter(offer => isShuttleOffer(offer));

    const passengerMap = new Map(allPassengers.map(p => [p.id, p]));

    const calculatePriceForPassenger = (passengerId: string, offers: IBookedOffer[]): number => {
        let total = 0;
        offers.forEach(offer => {
            offer.admissions?.forEach(admission => {
                if (admission.status !== "FULFILLED") return;
                splitAdmissionPerPassenger(admission).forEach(part => {
                    if (part.passengerId === passengerId) total += part.amount;
                });
            });
        });
        // Unrounded on purpose — rounding a share and then summing the rounded
        // shares drifts (50/3 three times over is 50.01). The row rounds once.
        return total;
    };

    // Helper function to get passenger type from age
    const getPassengerType = (passengerId: string): string => {
        const passenger = passengerMap.get(passengerId);
        if (!passenger) return "Adult";
        
        // Create a minimal passenger object for getUpperPassengerLabelByAge
        const passengerForType: ISelectedPassenger = {
            id: passenger.id,
            age: passenger.age,
            cards: passenger.cards || [],
            prmNeeds: passenger.prmNeeds || [],
            firstName: "",
            lastName: "",
            email: "",
            mobile: "",
            remember: false,
            seatingDetails: null
        };
        
        return getUpperPassengerLabelByAge(passenger.age || 0, passengerForType);
    };

    const combinedPriceDetails: PassengerPriceDetails[] = [];

    const hasAdmissionsForPassenger = (passengerId: string, offers: IBookedOffer[]): boolean => {
        return offers.some(offer => 
            offer.admissions?.some(admission => 
                admission.passengerIds?.includes(passengerId) && admission.status === "FULFILLED"
            )
        );
    };

    selectedPassengerIds.forEach(passengerId => {
        const trainPrice = calculatePriceForPassenger(passengerId, trainOffers);
        const hasTrainAdmission = hasAdmissionsForPassenger(passengerId, trainOffers);
        if (trainPrice > 0 || hasTrainAdmission) {
            combinedPriceDetails.push({
                pId: passengerId,
                pValue: trainPrice,
                pType: getPassengerType(passengerId),
                pStatus: "FULFILLED"
            });
        }

        // One entry per shuttle TICKET, not per passenger: a passenger with a
        // shuttle at both ends of a trip holds two tickets, and the load-time
        // builder counts them the same way.
        shuttleOffers.forEach(offer => {
            offer.admissions?.forEach(admission => {
                if (admission.status !== "FULFILLED") return;
                splitAdmissionPerPassenger(admission).forEach(part => {
                    if (part.passengerId !== passengerId) return;
                    combinedPriceDetails.push({
                        pId: passengerId,
                        pValue: part.amount,
                        pType: getPassengerType(passengerId),
                        pStatus: "FULFILLED",
                        isShuttle: true
                    });
                });
            });
        });
    });

    return combinedPriceDetails;
}

export function updatePaymentItemsForCancelBooking(passengerPriceList: PassengerPriceDetails[]): PaymentItemDetails[] {
    // Fare rows come from the train/admission entries only; shuttle entries are
    // tagged by calculateTrainAndShuttlePricesFromBooking and get their own row.
    // Never infer this from a booking-level flag: a booking CONTAINING a shuttle
    // is not a shuttle-only booking, and conflating the two relabelled every
    // fare row as "Shuttle ticket" on train+shuttle bookings.
    const outBoundPassengerPriceList = (passengerPriceList || []).filter((d) => !d?.isShuttle)
    const shuttleEntries = (passengerPriceList || []).filter((d) => d?.isShuttle)
    const outBoundPassengerDetails: PaymentItemDetails[] = []
    PASSENGER_TYPE_ROW_ORDER.forEach(pType => {
        const entries = outBoundPassengerPriceList.filter(d => d.pType === pType)
        if (entries.length === 0) return
        outBoundPassengerDetails.push({
            description: `${entries.length} ${PASSENGER_TYPE_ROW_LABELS[pType]}`,
            amount: roundAmount(entries.reduce((sum, d) => sum + (d.pValue || 0), 0))
        })
    })

    if (shuttleEntries.length > 0) {
        outBoundPassengerDetails.push({
            description: `${shuttleEntries.length} Shuttle`,
            amount: roundAmount(shuttleEntries.reduce((sum, d) => sum + (d.pValue || 0), 0))
        });
    }

    return outBoundPassengerDetails;
}
// "ecd62d51-f07f-4f77-a4e1-a4860e59aa17"

export function calculateDiscountPercentage(totalPriceOriginal: number, totalPrice: number) {
    try {
        const original = Number(totalPriceOriginal);
        const current = Number(totalPrice);

        // Guard invalid inputs
        if (!Number.isFinite(original) || original <= 0) return "";
        if (!Number.isFinite(current) || current <= 0 || current >= original) return "";

        const percentageCal = 1 - (current / original);
        const thirdDigit = Math.floor(((percentageCal * 1000) % 10));
        const adjusted = (thirdDigit >= 5)
            ? Math.ceil(percentageCal * 100)
            : Math.floor(percentageCal * 100);

        const per = Number.isFinite(adjusted) ? adjusted : 0;
        return per;
    } catch {
        return "";
    }
}

export function getCmsStopImageByStopId(stopId: string) {
    const cmsStops = safeGetRecoilStateValue<ICMSStops[]>("cmsStopsAtom");
    const filtredStop = cmsStops?.filter(t => t.code == stopId);
    if (filtredStop && filtredStop.length > 0) {
        // CMS images don't always expose a `small` format; fall back to the base
        // image url, then to '' (callers default to a placeholder image). Avoids
        // crashing on stops whose image is missing the `small` format entirely.
        const image = filtredStop[0]?.image?.[0];
        return image?.formats?.small?.url || image?.url || '';
    }
    return '';
}

export const getBookingTransportType = (booking: IBookingResponse): ITransportMode => {
    if (!booking?.booking.trips?.length) return "";
    const allPtModes: string[] = [];

    booking.booking.trips.forEach((trip: { legs: any[]; }) => {
        trip.legs?.forEach(leg => {
            const ptMode = leg?.timedLeg?.service?.mode?.ptMode;
            if (ptMode) {
                allPtModes.push(ptMode.toUpperCase());
            }
        });
    });
    const uniqueModes = Array.from(new Set(allPtModes));
    if (uniqueModes.length === 0) return "";
    if (uniqueModes.length === 1 && uniqueModes.includes("BUS")) return "ONLY_SHUTTLE";
    if (uniqueModes.length === 1 && uniqueModes.includes("TRAIN")) return "ONLY_TRAIN";
    if (uniqueModes.includes("BUS") && uniqueModes.includes("TRAIN")) return "SHUTTLE+TRAIN";

    return "";
};

// DEAD CODE — commented out on dev when the group-booking class dropdown
// switched to backend-supplied labels (TicketOptionManager) and this
// helper's last call site was removed. Wire-code form (SFC/SFF/STV →
// "standardValue"/"standardPro", PFF/PFP/PRV/PRS → "premium") preserved
// here so the function can be restored if the underlying duplicate-entry
// collision (multiple premium codes collapsing onto "premium") is fixed.
// export const getTrainClassTypeFormAPI = (productCode: string, serviceClassType?: string) => {
//     // Use serviceClass.type if provided (dynamic)
//     if (serviceClassType) {
//         if (serviceClassType === ServiceClassTypeEnum.STANDARD) return "standardValue"
//         if (serviceClassType === ServiceClassTypeEnum.HIGH) return "premium"
//     }
//     // Fallback to product code matching — same tier mapping as
//     // getTrainClassTypeFormProductCode (kept in lockstep).
//     if (productCode === ProductCodeEnum.SFC) return `standardValue`
//     else if (productCode === ProductCodeEnum.SFF || productCode === ProductCodeEnum.STV) return "standardPro"
//     else if (productCode === ProductCodeEnum.PFF || productCode === ProductCodeEnum.PFP || productCode === ProductCodeEnum.PRV || productCode === ProductCodeEnum.PRS) return "premium"
//     else return null
// }
export const hasCompartmentOffer = (
    passenger: IPassenger,
    placeMapResponse: PlaceMapResponse[] | undefined,
    segmentIndex: number,
    booking: IBookingResponse | undefined
): boolean => {
    if (!placeMapResponse || !placeMapResponse[segmentIndex] || !booking) {
        return false;
    }

    const currentPlaceMap = placeMapResponse[segmentIndex];
    const preSelections = currentPlaceMap?.vehicleAvailability?.preSelections || [];
    for (const offer of booking.booking?.bookedOffers || []) {
        for (const reservation of offer.reservations || []) {
            if (reservation.passengerIds?.includes(passenger.id)) {
                const matchingPreSelection = preSelections.find(
                    (preSel: any) => preSel.reservationId === reservation.id
                );

                if (matchingPreSelection) {
                    const place = matchingPreSelection.place;
                    return !!(place && (place.startsWith('Q') || place.startsWith('D')));
                }
            }
        }
    }

    return false;
};

export const findSeatCompartment = (
    seatId: string,
    coachNumber: string,
    placeMapResponse: PlaceMapResponse[] | undefined,
    segmentIndex: number
): { compartment: any; coach: any } | null => {
    if (!placeMapResponse || !placeMapResponse[segmentIndex]) {
        return null;
    }

    const currentPlaceMap = placeMapResponse[segmentIndex];
    const coach = currentPlaceMap?.vehicleAvailability?.vehicle?.coaches?.find(
        c => c.number === coachNumber
    );

    if (!coach) {
        return null;
    }

    for (const compartment of coach.compartments || []) {
        const place = compartment.places?.find(p => p.number === seatId);
        if (place) {
            return { compartment, coach };
        }
    }

    return null;
};


export const isPrivacySeating = (booking: IBookingResponse | undefined): boolean => {
    try {
        for (const offer of booking?.booking?.bookedOffers || []) {
            if (offer.reservations && offer.reservations.length > 0) {
                for (const reservation of offer.reservations) {
                    const placeAllocation = reservation.placeAllocation;
                    if (placeAllocation && placeAllocation.accommodationSubType === "COMPARTMENT") {
                        return true;
                    }
                }
            }
        }
        return false;
    } catch (error) {
        return false;
    }
};

export const isPrivacySeatingV2 = (booking: IBookingResponse | undefined, segmentIndex: number): boolean => {
    try {
        const trips = booking?.booking?.trips || [];
        const currentTrip = trips[segmentIndex];
        if (!currentTrip) {
            return false;
        }

        const currentTripId = currentTrip.id;

        for (const offer of booking?.booking?.bookedOffers || []) {
            if (offer.tripCoverage?.coveredTripId !== currentTripId) {
                continue;
            }

            const hasCollectiveOfferMode = offer.admissions?.some(
                admission => admission.offerMode === "COLLECTIVE"
            );
            if (hasCollectiveOfferMode) {
                return true;
            }
        }
        return false;
    } catch (error) {
        return false;
    }
};

export const isShuttleAvailable = (stationId: string, stops: StationsListType) => {
    if (!stops || !stationId) return false;

    const station = stops.find(stop => stop.id === stationId);
    if (!station) return false;

    return station.subStations && station.subStations.some(subStation => subStation.type === 'bus');
  };


export const getPassengerSortValue = (passengerType?: string) => {
    const pType = (passengerType || "").toLowerCase();
    if (pType.includes("adults")) return 1;
    if (pType.includes("children")) return 2;
    if (pType.includes("infants")) return 3;
    if (pType.includes("seniorcitizens")) return 4;
    if (pType.includes("students") || pType.includes("student")) return 5;
    if (pType.includes("reducedmobility")) return 6;
    if (pType.includes("wheelchair")) return 7;
    return 8;
  };

/**
 * Returns true when a trip direction string represents the inbound direction,
 * accepting both API formats:
 *   - TripTypeEnum.IN_BOUND  ("IN_BOUND")  — Retailer / exchange-operations API
 *   - TripDirectionTypeEnum.INBOUND ("INBOUND") — Transcity / booking-summary API
 *
 * Use this instead of duplicating the dual-enum check across every Action that
 * resolves addon/fee direction.
 */
export function isInbound(direction: string | undefined | null): boolean {
  return direction === TripTypeEnum.IN_BOUND || direction === TripDirectionTypeEnum.INBOUND;
}

/**
 * Returns true when a trip direction string represents the outbound direction,
 * accepting both API formats:
 *   - TripTypeEnum.OUT_BOUND  ("OUT_BOUND")  — Retailer / exchange-operations API
 *   - TripDirectionTypeEnum.OUTBOUND ("OUTBOUND") — Transcity / booking-summary API
 */
export function isOutbound(direction: string | undefined | null): boolean {
  return direction === TripTypeEnum.OUT_BOUND || direction === TripDirectionTypeEnum.OUTBOUND;
}
